import type { QuestionStatus, OrderType } from '@exam-management/graphql';
import queryString, { type ParsedQuery } from 'query-string';
import { useCallback, useMemo } from 'react';
import { useSearchParams, type URLSearchParamsInit } from 'react-router-dom';

import { initialQContentList, type QuestionCategoryInput } from '../../constants';

export interface QueryParam {
  name: string;
  value: unknown;
}

const initialQContentSearch = queryString.stringify(initialQContentList, {
  arrayFormat: 'bracket',
});

function mapToSearchingOptions(params: ParsedQuery<string | number | boolean>) {
  const page = (params['page'] ?? initialQContentList.page) as number;
  const pageSize = (params['pageSize'] ?? initialQContentList.pageSize) as number;

  const field = (params['field'] ?? initialQContentList.field) as string;
  const sort = ((params['sort'] ?? initialQContentList.sort) as string).toUpperCase() as OrderType;

  const qIds = (params['qIds'] ?? []) as string[];
  const status = params['status'] as QuestionStatus;
  const types = (params['type'] ?? []) as [];
  const part = (params['part'] ?? initialQContentList.part) as string;
  const subject = (params['subject'] ?? initialQContentList.subject) as string;
  const group = (params['group'] ?? initialQContentList.group) as string;
  const chapter = (params['chapter'] ?? initialQContentList.chapter) as string;
  const topic = (params['topic'] ?? initialQContentList.topic) as string;
  const subtopic = (params['subtopic'] ?? initialQContentList.subtopic) as string;

  const category = (params['category'] ?? initialQContentList.category) as QuestionCategoryInput;

  return {
    part,
    subject,
    group,
    chapter,
    topic,
    subtopic,
    status,
    page,
    pageSize,
    field,
    qIds,
    sort,
    types,
    category,
  };
}

export default function useQContentSearchParams(initialParams?: URLSearchParamsInit) {
  const [searchParams, setSearchParams] = useSearchParams(initialParams ?? initialQContentSearch);

  const searchParamsObject = useMemo(() => {
    const baseParams = queryString.parse(searchParams.toString(), {
      arrayFormat: 'bracket',
      parseBooleans: true,
      parseNumbers: true,
    });

    return mapToSearchingOptions(baseParams);
  }, [searchParams]);

  const setSearchQueryParams = useCallback(
    (queryParams: QueryParam[]) => {
      const params = new URLSearchParams(searchParams);

      for (const { name, value } of queryParams) {
        if (value) {
          if (Array.isArray(value)) {
            params.delete(name + '[]');
            const filterList: string[] = value.map((statusObj) => statusObj);
            const stringifiedFilterList = queryString.stringify(
              { [name]: filterList },
              { arrayFormat: 'bracket' },
            );

            const parsedParams = new URLSearchParams(stringifiedFilterList);
            for (const [key, value] of parsedParams) {
              params.append(key, value);
            }
          } else {
            params.set(name, value as string);
          }
        } else {
          params.delete(name);
        }
      }
      const query = params.toString();
      setSearchParams(query);
    },
    [searchParams, setSearchParams],
  );

  return [searchParamsObject, setSearchQueryParams] as const;
}
